import { Button, ButtonGroup, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';
import React, { useState, useEffect } from 'react';
import PageContainer from '../../../components/PageContainer';
import { useSnackbar } from 'notistack';
import { connect } from 'react-redux';
import CaseTable from '../../../components/CaseTable';
import { getAllBank2BankBreachedCases, getUnassignedBank2BankBreachedCases } from '../../../redux/api/cases';
import { getPrivilegeStatus } from '../../../util';
import { getAllInstitutions } from '../../../redux/api/institution';
import { InfoOutlined } from '@mui/icons-material';

const sections = [
    {
        name: 'All B2B Breached Cases',
        id: 'getAllBank2BankBreachedCases',
    },
    {
        name: 'Unassigned B2B Breached Cases',
        id: 'getUnassignedBank2BankBreachedCases',
    },
];

const Bank2BankBreachedCases = ({ user }) => {
    const [processing, setProcessing] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const [records, setRecords] = useState([]);
    const [searchValue, setSearchValue] = useState([]);
    const [section, setSection] = useState(0);
    const [paginationData, setPaginationData] = useState({});
    const [unassignedPaginationData, setUnassignedPaginationData] = useState({});
    const [institutions, setInstitutions] = useState([]);
    const [selectedCase, setSelectedCase] = useState(null);
    const [open, setOpen] = useState(false);
    const [unassignedRecords, setUnassignedRecords] = useState([]);

    const init = async (start = 0, length = user.paginationSize) => {
        try {
            setProcessing(true);
            const res = await getAllBank2BankBreachedCases({ start, length }, false);
            const resp = await getAllInstitutions();
            setRecords(records?.length == 0 ? res?.resultObject?.dataTableResponse?.data || [] : records.concat(res?.resultObject?.dataTableResponse?.data || []));

            setInstitutions(resp?.resultObject || []);
            setPaginationData({
                start,
                length,
                total: res?.resultObject?.dataTableResponse?.recordsTotal,
                totalFiltered: res?.resultObject?.dataTableResponse?.recordsFiltered,
            });
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setProcessing(false);
        }
    };

    const fetchUnassigned = async (start = 0, length = user.paginationSize) => {
        try {
            setProcessing(true);
            const res = await getUnassignedBank2BankBreachedCases({
                start,
                length,
            });

            setUnassignedRecords(unassignedRecords?.length == 0 ? res?.resultObject?.dataTableResponse?.data || [] : unassignedRecords.concat(res?.resultObject?.dataTableResponse?.data || []));

            setUnassignedPaginationData({
                start,
                length,
                total: res?.resultObject?.recordsTotal,
                totalFiltered: res?.resultObject?.recordsFiltered,
            });
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setProcessing(false);
        }
    };

    const getExportFunction = async (section) => {
        try {
            let resp;
            switch (section) {
                case 0:
                    resp = await getAllBank2BankBreachedCases(searchValue, true);
                    enqueueSnackbar(resp?.message || 'Your report is being processed, kindly check the reports page for progress');
                    break;
                case 1:
                    resp = await getUnassignedBank2BankBreachedCases(searchValue, true);
                    enqueueSnackbar(resp?.message || 'Your report is being processed, kindly check the reports page for progress');
                    break;
                default:
                    break;
            }
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        }
    };

    const search = async (searches, closeSearchModal) => {
        try {
            setSearchValue(searches);

            const resp = section == 0 ? await getAllBank2BankBreachedCases({ searches }, true) : await getUnassignedBank2BankBreachedCases({ searches }, true);
            if (resp?.statusCode == '000') {
                section == 0 ? setRecords(resp?.resultObject?.data || []) : setUnassignedRecords(resp?.resultObject?.data || []);
                enqueueSnackbar(resp?.message || 'Your report is being processed, kindly check the reports page for progress');
                closeSearchModal();
                if (resp?.resultObject?.data?.length == 0) {
                    enqueueSnackbar('No result found for search parameters', {
                        variant: 'info',
                    });
                }
            } else {
                enqueueSnackbar(resp?.message || 'An error occurred', {
                    variant: 'error',
                });
            }
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        }
    };

    useEffect(() => {
        init();
        fetchUnassigned();
    }, [user.paginationSize]);

    return (
        <PageContainer pageTitle={'Bank to bank Breached Cases'} processing={processing}>
            <ButtonGroup variant="outlined" className="pl-2 pr-2 mb-2" fullWidth>
                {sections.map(
                    (sec, key) =>
                        getPrivilegeStatus(sec.id) && (
                            <Button variant={section === key ? 'contained' : 'outlined'} key={key} onClick={() => setSection(key)}>
                                {sec.name}
                            </Button>
                        )
                )}
            </ButtonGroup>

            <CaseTable
                additionalActions={(params) => (
                    <>
                        <IconButton
                            color="info"
                            title="Report details"
                            onClick={() => {
                                setSelectedCase(params);
                                setOpen(true);
                            }}
                        >
                            <InfoOutlined />
                        </IconButton>
                    </>
                )}
                customColumns={[
                    {
                        field: 'trackingReferenceNumber',
                        headerName: 'Tracking Reference No.',
                        width: 250,
                    },
                    {
                        field: 'rInstitution',
                        headerName: 'Reporting Institution',
                        width: 250,
                        valueGetter: (params) =>
                            params?.row?.bankToBankComplaint?.institutionFor?.name || institutions?.find((r) => r?.institutionId == params?.row?.bankToBankComplaint?.fI_For)?.name || '',
                    },
                    {
                        field: 'institution',
                        headerName: 'Reported Institution',
                        width: 250,
                        valueGetter: (params) =>
                            params?.row?.bankToBankComplaint?.institutionAgainst?.name || institutions?.find((r) => r?.institutionId == params?.row?.bankToBankComplaint?.fI_Against)?.name || '',
                    },
                    {
                        field: 'title',
                        headerName: 'Title',
                        width: 200,
                        valueGetter: (params) => params?.row?.bankToBankComplaint?.title || '',
                    },
                    {
                        field: 'subcategoryCode',
                        headerName: 'Subcategory Code',
                        width: 200,
                        valueGetter: (params) => params?.row?.bankToBankComplaint?.subcategoryCode || '',
                    },
                    { field: 'caseId', headerName: 'Case ID', width: 250, renderCell: (params) => <span>{params?.row?.caseId || ''}</span> },
                ]}
                addActionColumn
                cases={section == 0 ? records : unassignedRecords}
                showPendingActionsBtn={false}
                showAssignBtn
                showAssignFooterBtn
                showCloseCaseBtn={false}
                showDeEscalateCaseBtn={false}
                showEscalateCaseBtn={false}
                showResolveCaseBtn={false}
                showAttachBtn={false}
                showConversationBtn={false}
                selectable
                exportFunction={async () => await getExportFunction(section)}
                searchFunction={search}
                initFunction={init}
                showRecategorizeCaseBtn
                paginationData={section == 0 ? paginationData : unassignedPaginationData}
            />

            <Dialog open={open} maxWidth="md">
                <DialogTitle>Reported Case Information</DialogTitle>
                <DialogContent>
                    <p>
                        <b>Title:</b> {selectedCase?.bankToBankComplaint?.title}
                    </p>
                    <p>
                        <b>Subcategory:</b> {selectedCase?.bankToBankComplaint?.subcategoryCode}
                    </p>
                    <p>
                        <b>Reporting Institution:</b>{' '}
                        {selectedCase?.bankToBankComplaint?.institutionFor?.name || institutions?.find((r) => r?.institutionId == selectedCase?.bankToBankComplaint?.fI_For)?.name || ''}
                    </p>
                    <p>
                        <b>Reported Institution:</b>{' '}
                        {selectedCase?.bankToBankComplaint?.institutionAgainst?.name || institutions?.find((r) => r?.institutionId == selectedCase?.bankToBankComplaint?.fI_Against)?.name || ''}
                    </p>
                    <p>
                        <b>Message:</b> {selectedCase?.bankToBankComplaint?.message}
                    </p>
                </DialogContent>
                <DialogActions className="pr-1 pb-1">
                    <Button
                        color="secondary"
                        variant="contained"
                        onClick={() => {
                            setOpen(false);
                            setSelectedCase(null);
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
};

export default connect(({ user }) => ({ user }))(Bank2BankBreachedCases);
